// CBootScene — 預先載入 MVP 所需全部素材
// 對應 PRD §4.2 CBootScene
//
// === 使用圖片 ===
// 載入畫面由 createTableBackground / Graphics / Text 繪製，無直接圖片依賴。
// 各預載 asset key 與路徑集中列在 preload() 的用途分區。
// 載入清單（依用途分組）：
// 1. UI：home-bg、table-a、按鈕底、各種 icon、金幣圖示 coin.png、九位牌友頭像 assets/avatars/*.png、商店商品圖 shop-card／shop-pack-s／m／l（Codex 產圖）
// 2. tile sprites：tiles-source 切成 spritesheet（6×7 grid，42 張牌面）
// 3. tile back / side：牌背、側邊（顯示對家牌使用）
// 4. 動畫：擲骰、補花、聽牌、自摸、胡牌、放槍、慶祝粒子（assets/fx/）
// 5. 音效：三首 BGM、擲骰、風局、10 組角色語音、2 組開局語音與 UI 音效
//
// 載入完跳 CHomeScene。

import Phaser from 'phaser';
import { CHARACTERS } from '@core/characters';
import { COIN_ICON_KEY } from '@ui/home-account-bar';
import { avatarKey } from '@ui/seat-characters';
import { SHOP_CARD_KEY, SHOP_PACK_L_KEY, SHOP_PACK_M_KEY, SHOP_PACK_S_KEY } from './shop-scene';
import { createTableBackground, TABLE_THEME } from '@ui/table-background';
import {
  AudioKey,
  START_VOICE_CUES,
  START_VOICE_PACKS,
  startVoiceAudioKey,
  VOICE_CUES,
  VOICE_PACKS,
  voiceAudioKey,
} from '@audio/audio-keys';
import { Wind, type TWind } from '@core/types';

import { SceneKey } from './scene-keys';

/**
 * tiles-source.png 是 6×7 grid 包含 42 個牌面。
 * 寬 378 / 高 539 → 每格約 63×77。
 *
 * frame index 對應（從左到右、從上到下）：
 *   0–8   man 1-9
 *   9–17  pin 1-9
 *   18–26 sou 1-9
 *   27–30 wind 東南西北
 *   31–33 dragon 中發白
 *   34–41 flower 春夏秋冬梅蘭菊竹（rank 1-8）
 */
export const TILE_SHEET_KEY = 'tile-sheet';
export const TILE_FRAME_WIDTH = 63;
export const TILE_FRAME_HEIGHT = 77;

// table-text spritesheet：216×216 圖，切成 4×4 格、每格 54×54
// 風字 frame index 對應（row 1 normal）：東=0、南=1、西=2、北=3
export const TABLE_TEXT_KEY = 'table-text';
export const TABLE_TEXT_FRAME_SIZE = 54;
export const TableTextFrame = {
  // row 1：東 南 西 北（normal）
  EastNormal: 0,
  SouthNormal: 1,
  WestNormal: 2,
  NorthNormal: 3,
  // row 2：東 南 西 北（active 高亮）
  EastActive: 4,
  SouthActive: 5,
  WestActive: 6,
  NorthActive: 7,
  // row 3：風 局 底 台
  Wind: 8,
  Round: 9,
  Base: 10,
  Fan: 11,
} as const;
export type TTableTextFrame = (typeof TableTextFrame)[keyof typeof TableTextFrame];

// table-number spritesheet：135×108，2×5 grid，每格 27×54
// frame 0–4：0 1 2 3 4  /  frame 5–9：5 6 7 8 9（直接對應 digit）
export const TABLE_NUMBER_KEY = 'table-number';
export const TABLE_NUMBER_FRAME_WIDTH = 27;
export const TABLE_NUMBER_FRAME_HEIGHT = 54;

// table-logo：216×72，桌面中央 logo（白底，使用時 tint 黑色半透明）
export const TABLE_LOGO_KEY = 'table-logo';

// number-4 spritesheet：792×108，1×11 grid，每格 72×108（已 Upscayl 2× 放大，原 396×54）
// frame 0–9 對應數字 0–9、frame 10 是逗號 ','
// ⚠️ frame 尺寸是「素材原生像素」（2× 後）；使用端要相對應減半 scale，避免 UI 跟著放大
export const NUMBER_4_KEY = 'number-4';
export const NUMBER_4_FRAME_WIDTH = 72;
export const NUMBER_4_FRAME_HEIGHT = 108;
export const NUMBER_4_FRAME_COMMA = 10; // 逗號（千分位分隔）

// 單張字圖
export const WORD_X_KEY = 'word-x'; // 「x」字（搭配 number-4 用於 "x N" 計數顯示）

// 牌面高亮邊框（玩家可選手牌 / 放大預覽用）— 黃色圓角框 81×117
export const CARD_FRAME_KEY = 'card-frame';

// option spritesheet：5×3 grid，每格 133×133（圖片實際 666×400，整除誤差最後 1 px）
// row 0 (frames  0– 4)：吃 / 碰 / 槓 / 聽 / 胡（彩色 normal）
// row 1 (frames  5– 9)：吃 / 碰 / 槓 / 聽 / 胡（灰色 disabled）
// row 2 (frames 10–14)：焦點純邊框 / 放棄 / 離開 / 代打 / 取消
export const OPTION_KEY = 'option';
export const OPTION_FRAME_WIDTH = 133;
export const OPTION_FRAME_HEIGHT = 133;
export const OptionFrame = {
  ChiNormal: 0,
  PongNormal: 1,
  KongNormal: 2,
  ListenNormal: 3,
  HuNormal: 4,
  ChiDisabled: 5,
  PongDisabled: 6,
  KongDisabled: 7,
  ListenDisabled: 8,
  HuDisabled: 9,
  Focus: 10,
  Pass: 11,
  Exit: 12,
  AiProxy: 13,
  Cancel: 14,
} as const;
export type TOptionFrame = (typeof OptionFrame)[keyof typeof OptionFrame];

// direction-hand spritesheet：2×2 grid，每格 144×126（原圖 288×252 直接複製，未放大）
// 回合指示手勢：輪到誰出牌就在該家風字旁顯示。frame index = 相對玩家的座位方向：
// 0 本家（拳頭朝下）/ 1 下家（食指朝右）/ 2 對家（食指朝上）/ 3 上家（食指朝右）
export const DIRECTION_HAND_KEY = 'direction-hand';
export const DIRECTION_HAND_FRAME_WIDTH = 144;
export const DIRECTION_HAND_FRAME_HEIGHT = 126;
export const DirectionHandFrame = {
  Self: 0, // 本家（下）
  Next: 1, // 下家（右）
  Across: 2, // 對家（上）
  Prev: 3, // 上家（左）
} as const;
export type TDirectionHandFrame = (typeof DirectionHandFrame)[keyof typeof DirectionHandFrame];

// action icon spritesheet：810×144 圖，1×5 grid、每格 162×144（吃 / 碰 / 槓 / 聽 / 花）
// 用途：別家（非本家）吃 / 碰 / 槓 時，在該家風字位置淡入淡出顯示對應字樣
// 來源 asset/reference/icon_action.png 直接複製，未放大
export const ACTION_ICON_KEY = 'icons-action';
export const ACTION_ICON_FRAME_WIDTH = 162;
export const ACTION_ICON_FRAME_HEIGHT = 144;
export const ActionIconFrame = {
  Chi: 0, // 吃
  Pong: 1, // 碰
  Kong: 2, // 槓
  Listen: 3, // 聽
  Flower: 4, // 花
} as const;
export type TActionIconFrame = (typeof ActionIconFrame)[keyof typeof ActionIconFrame];

// 補花橫幅（CFlowerBanner 用）— 來源 asset/reference/{icon_flower,flower01,flower02,flower_bg}.png
export const FLOWER_EMBLEM_KEY = 'flower-emblem'; // 圓形「補花」徽章（270×198）
export const FLOWER_PETAL_A_KEY = 'flower-petal-a'; // 暗紅梅花（90×90）
export const FLOWER_PETAL_B_KEY = 'flower-petal-b'; // 粉紅梅花（90×90）
export const FLOWER_BAND_KEY = 'flower-band'; // 直向粉紅漸層條（54×108，橫向鋪成花帶底）

// 聽牌模式素材（來源 asset/reference/{hand,thumb01,focus_ready_hand}.png；hand/thumb 經 Upscayl 4×）
export const PEEK_HAND_KEY = 'peek-hand'; // 瞇牌：墊在摸牌「後面」的握牌手掌（612×1116）
export const PEEK_THUMB_KEY = 'peek-thumb'; // 瞇牌：蓋在摸牌「前面」、繞上方圓心往左轉掀牌的拇指（360×720）
export const TENPAI_BANNER_KEY = 'tenpai-banner'; // 宣告「聽」橫幅底圖：橘色速度線（1280×108）
// 聽牌橫幅疊加層（比照原版 ListenAnimEntity：底圖 + 隨機臉 + 脈動星）
export const TENPAI_FACE_KEY = 'tenpai-face'; // 隨機臉：576×216 → 1×2 grid、每格 576×108（兩張臉隨機選一）
export const TENPAI_FACE_FRAME_WIDTH = 576;
export const TENPAI_FACE_FRAME_HEIGHT = 108;
export const TENPAI_STAR_KEY = 'tenpai-star'; // 脈動星：162×216 → 3×4 grid、每格 54×54（取單格脈動）
export const THINKING_ICON_KEY = 'icon-thinking'; // AI 思考狀態：504×351 → 左右 2 frame、每格 252×351
export const TENPAI_STAR_FRAME_WIDTH = 54;
export const TENPAI_STAR_FRAME_HEIGHT = 54;

// ============ 擲骰開局過場（CDiceScene）素材 ============
// 來源：至尊麻將王反編譯 extracted-sprites（texture2/texture3），原樣複製未放大。
// dice-dish：骰盅單張（342×342）
export const DICE_DISH_KEY = 'dice-dish';
// dice-faces：靜止點數 3×2 grid，每格 108×108（6 面，落定後依已保存 round plan 顯示）
export const DICE_FACES_KEY = 'dice-faces';
export const DICE_FACE_FRAME_SIZE = 108;
// dice-roll：滾動殘影動畫 6×1 grid，每格 108×108（飛行中循環播放）
export const DICE_ROLL_KEY = 'dice-roll';
export const DICE_ROLL_FRAME_SIZE = 108;
// round-words：圈風/局字 3×2 grid，每格 144×144
//   frame 0–3：東 南 西 北（風 rank-1 對應）/ frame 4：風 / frame 5：局
export const ROUND_WORDS_KEY = 'round-words';
export const ROUND_WORDS_FRAME_SIZE = 144;
export const RoundWordFrame = {
  East: 0,
  South: 1,
  West: 2,
  North: 3,
  Wind: 4, // 「風」
  Round: 5, // 「局」
} as const;
export type TRoundWordFrame = (typeof RoundWordFrame)[keyof typeof RoundWordFrame];

// ============ 牌局結束三畫面（結算流程）素材 ============
// 來源：asset/reference/*.png（至尊麻將王 HD 原始結算畫面拆圖）

// result-avatar：126×63，1×2 grid，每格 63×63（玩家頭像）
export const RESULT_AVATAR_KEY = 'result-avatar';
export const RESULT_AVATAR_FRAME_SIZE = 63;
export const ResultAvatarFrame = {
  Yellow: 0, // 黃色頭像（亮）
  Gray: 1, // 灰色頭像（暗）
} as const;
export type TResultAvatarFrame = (typeof ResultAvatarFrame)[keyof typeof ResultAvatarFrame];

// result-seat-text：252×126，2×2 grid，每格 126×63（自家/下家/對家/上家 橘字）
// frame 依「相對方位」排（非絕對 seat）：自家=0、下家=1、對家=2、上家=3，
// 直接用 seatToRelative(seat, playerSeat) 算出的相對序當 frame（見 base-result-scene）。
export const RESULT_SEAT_TEXT_KEY = 'result-seat-text';
export const RESULT_SEAT_TEXT_FRAME_WIDTH = 126;
export const RESULT_SEAT_TEXT_FRAME_HEIGHT = 63;

// result-wind：126×126，2×2 grid，每格 63×63（黃底東南西北大風字）
export const RESULT_WIND_KEY = 'result-wind';
export const RESULT_WIND_FRAME_SIZE = 63;
export const ResultWindFrame = {
  east: 0,
  south: 1,
  west: 2,
  north: 3,
} as const;
export type TResultWindFrame = (typeof ResultWindFrame)[keyof typeof ResultWindFrame];

export const ICON_DEALER_KEY = 'icon-dealer'; // 莊家「莊」字標（seat-badges / 結算 header 共用）
export const RESULT_WIN_KEY = 'result-win'; // 籌碼結算「勝」章
export const RESULT_LOSE_KEY = 'result-lose'; // 籌碼結算「敗」章
export const RESULT_DRAW_KEY = 'result-draw'; // 流局指示圖（126×126）
export const RESULT_TABLE_KEY = 'result-table'; // 原 APP 綠／紅桌布，2×1 grid
export const RESULT_TABLE_FRAME_WIDTH = 288;
export const RESULT_TABLE_FRAME_HEIGHT = 360;

// 單張字圖 / 面板（不切格）
export const RESULT_TITLE_KEY = 'result-title'; // 「結算」標題（108×45）
export const RESULT_NPC_KEY = 'result-npc'; // 兔女郎 NPC（360×315）
export const RESULT_NPC_BUBBLE_KEY = 'result-npc-bubble'; // NPC 右尾巴黃色對話氣泡
export const RESULT_NPC_TIP_KEY = 'result-npc-tip'; // 「繼續再玩一局嗎？」原 APP 字圖
// 2D 奶油粗框素材：frame 排除產圖透明留白，九宮格不修改來源圖。
export const CREAM_PANEL_KEY = 'cream-panel'; // 奶油框底（共用元件以九宮格調整粗度）
export const CREAM_BUTTON_RED_KEY = 'cream-button-red'; // 主操作與選中
export const CREAM_BUTTON_GREEN_KEY = 'cream-button-green'; // 次操作與未選中
export const CREAM_MASCOT_PEEK_KEY = 'cream-mascot-peek'; // 對話框探頭角色
export const CREAM_MASCOT_KEY = 'cream-mascot'; // 首頁按鈕小牌角色
export const CREAM_ASSET_FRAME = 'cream-content'; // 可見素材範圍
const CREAM_ASSETS = [
  {
    key: CREAM_PANEL_KEY,
    path: 'assets/ui/cream-panel.png',
    x: 45,
    y: 52,
    width: 1594,
    height: 824,
  },
  {
    key: CREAM_BUTTON_RED_KEY,
    path: 'assets/ui/cream-button-red.png',
    x: 51,
    y: 86,
    width: 2070,
    height: 547,
  },
  {
    key: CREAM_BUTTON_GREEN_KEY,
    path: 'assets/ui/cream-button-green.png',
    x: 76,
    y: 106,
    width: 2019,
    height: 506,
  },
  {
    key: CREAM_MASCOT_PEEK_KEY,
    path: 'assets/ui/cream-mascot-peek.png',
    x: 283,
    y: 157,
    width: 998,
    height: 707,
  },
  {
    key: CREAM_MASCOT_KEY,
    path: 'assets/ui/cream-mascot.png',
    x: 76,
    y: 137,
    width: 968,
    height: 1145,
  },
] as const;

export const WOOD_BUTTON_KEY = 'button-carved-wood'; // 純木雕按鈕共用素材
export const WOOD_BUTTON_HEIGHT = 632; // 裁切後木牌高度
export const WOOD_BUTTON_CAP = 180; // 三宮格保留左右木雕圓角
export const SETTINGS_SWITCH_RAIL_KEY = 'settings-switch-rail'; // 純胡桃木凹槽
export const SETTINGS_SWITCH_THUMB_KEY = 'settings-switch-thumb'; // 淺色楓木圓鈕
export const SETTINGS_SWITCH_FRAME = 'trimmed'; // 排除透明留白的素材 frame
export const SETTINGS_SWITCH_RAIL_HEIGHT = 616; // 軌道裁切後原始高度
export const SETTINGS_SWITCH_RAIL_CAP = 320; // 左右半圓端點，三宮格保留形狀
export const DIALOG_WALNUT_GOLD_KEY = 'dialog-walnut-gold'; // 對話框共用木紋金框
export const RESULT_DIALOG_KEY = 'result-dialog'; // 金框綠底面板（270×270，當 ninepatch 拉伸用）
export const RESULT_TAG_SELF_DRAW_KEY = 'result-tag-self-draw'; // 「自摸」章（1080×792，4×）
export const RESULT_TAG_HU_KEY = 'result-tag-hu'; // 「胡」章（1080×792，4×，紫）
export const RESULT_TAG_PAO_KEY = 'result-tag-pao'; // 「放槍」章（1080×792，4×，綠）
export const RESULT_NUMBER_SYMBOL_KEY = 'result-number-symbol'; // ＋／－，2×1 grid
export const RESULT_NUMBER_SYMBOL_FRAME_WIDTH = 72;
export const RESULT_NUMBER_SYMBOL_FRAME_HEIGHT = 108;
export const RESULT_BTN_NORMAL_KEY = 'result-btn-normal'; // 紅色按鈕底（162×108）
export const RESULT_BTN_PRESS_KEY = 'result-btn-press'; // 紅色按鈕底（按下）
export const RESULT_BTN_CONTINUE_KEY = 'result-btn-continue'; // 「繼續」字（126×63）
export const RESULT_BTN_EXIT_KEY = 'result-btn-exit'; // 「離開」字（126×63）
export const RESULT_BTN_HOVER_KEY = 'result-btn-hover'; // hover 整張紅底+金框合成圖（162×108，與按鈕底同尺寸）
export const HOME_BUTTON_START_FRAME_KEY = 'home-btn-start-frame'; // 首頁開始鈕同尺寸純邊框
export const HOME_BUTTON_SMALL_FRAME_KEY = 'home-btn-small-frame'; // 首頁設定鈕同尺寸純邊框
export const HOME_BUTTON_GENERIC_FRAME_KEY = 'home-btn-generic-frame'; // 對話框／設定控制項純邊框

// 全域預設主音量（0–1）：boot 套用到 game 層級 SoundManager，影響所有場景的音效 / 語音 / BGM
export const DEFAULT_MASTER_VOLUME = 0.05;

// 擲骰開局音效（自原版 APK assets/mfx 抽出）
export const SFX_DICE_KEY = 'sfx-dice'; // 骰子搖落聲（mfx/dice.mp3）
export const SFX_OPEN_WIND_KEY_BY_ROUND: Record<TWind, Record<TWind, string>> = {
  [Wind.East]: {
    [Wind.East]: 'sfx-round-east-east',
    [Wind.South]: 'sfx-round-east-south',
    [Wind.West]: 'sfx-round-east-west',
    [Wind.North]: 'sfx-round-east-north',
  },
  [Wind.South]: {
    [Wind.East]: 'sfx-round-south-east',
    [Wind.South]: 'sfx-round-south-south',
    [Wind.West]: 'sfx-round-south-west',
    [Wind.North]: 'sfx-round-south-north',
  },
  [Wind.West]: {
    [Wind.East]: 'sfx-round-west-east',
    [Wind.South]: 'sfx-round-west-south',
    [Wind.West]: 'sfx-round-west-west',
    [Wind.North]: 'sfx-round-west-north',
  },
  [Wind.North]: {
    [Wind.East]: 'sfx-round-north-east',
    [Wind.South]: 'sfx-round-north-south',
    [Wind.West]: 'sfx-round-north-west',
    [Wind.North]: 'sfx-round-north-north',
  },
};
export const SFX_RESULT_MONEY_KEY = 'sfx-result-money'; // 原 APP 籌碼跑分循環聲（mfx/money.ogg）

// 牌局結束儀式（自原版 APK 圖集／mfx 抽出）
// 三種印記各保留原始 270×198 與 Upscayl 1080×792：
// 放大動畫用 4×，接近靜止尺寸後切回原始圖，避免 WebGL 大幅縮圖造成 aliasing。
export const ROUND_SELF_DRAW_KEY = 'round-self-draw'; // 「自摸」原始印記
export const ROUND_SELF_DRAW_4X_KEY = 'round-self-draw-4x'; // 「自摸」4× 動畫印記
export const ROUND_HU_KEY = 'round-hu'; // 「胡」原始印記
export const ROUND_HU_4X_KEY = 'round-hu-4x'; // 「胡」4× 動畫印記
export const ROUND_CHUCKER_KEY = 'round-chucker'; // 「放槍」原始印記
export const ROUND_WIN_FAN_KEY = 'round-win-fan'; // 自摸放射扇光
export const ROUND_WIN_BAND_KEY = 'round-win-band'; // 自摸橫向紅黑漸層底
export const ROUND_WIN_STARS_KEY = 'round-win-stars'; // 3×4 星光 spritesheet（每格 216×216）
export const ROUND_WIN_STAR_FRAME_SIZE = 216; // Upscayl 4× 後單格尺寸
// 慶祝粒子（ChatGPT Image 依牌小人畫風生成、去洋紅背景後切成單張；約 130～180 px）
export const FxKey = {
  StarA: 'fx-star-a', // 金色五角星
  StarB: 'fx-star-b', // 金色五角星（另一筆觸）
  CoinA: 'fx-coin-a', // 方孔金幣（正面）
  CoinB: 'fx-coin-b', // 方孔金幣（斜角）
  SparkleCream: 'fx-sparkle-cream', // 奶油色四角閃光
  SparkleRed: 'fx-sparkle-red', // 紅色四角閃光
} as const;
export type TFxKey = (typeof FxKey)[keyof typeof FxKey];
// 流局儀式（自原版圖集抽出；圖片經 Upscayl digital-art-4x 放大）
export const ROUND_DRAW_BAND_KEY = 'round-draw-band'; // 藍色橫向漸層底
export const ROUND_DRAW_WAVES_KEY = 'round-draw-waves'; // 2-frame 波浪 spritesheet
export const ROUND_DRAW_WAVE_FRAME_WIDTH = 576; // 原始每格 144×180，Upscayl 4×
export const ROUND_DRAW_WAVE_FRAME_HEIGHT = 720;
export const ROUND_DRAW_MARK_KEY = 'round-draw-mark'; // 「流局」印記

export const HOME_MAIN_BG_KEY = 'home-main-bg'; // 首頁原版素材
export const HOME_CURTAIN_TOP_KEY = 'home-curtain-top'; // 首頁原版素材
export const HOME_CURTAIN_SIDE_KEY = 'home-curtain-side'; // 首頁原版素材
export const HOME_NPC_KEY = 'home-npc'; // 首頁原版素材
export const HOME_NPC_EARS_KEY = 'home-npc-ears'; // 首頁原版素材
export const HOME_FOCUS_STAR_KEY = 'home-focus-star'; // 首頁原版素材

export const HOME_TITLE_KEY = 'home-title'; // 首頁原 logo
export const HOME_CARDS_KEY = 'home-cards'; // 首頁牌扇圖集

const LOADING_BAR_WIDTH = 400; // 載入條寬
const LOADING_BAR_HEIGHT = 12; // 載入條高
const LOADING_BAR_INSET = 3; // 金框內縮
const LOADING_BAR_Y_OFFSET = 30; // 載入條相對中央下移
const LOADING_TITLE_OFFSET = 90; // 遊戲名稱相對載入條上移
const LOADING_LABEL_OFFSET = 36; // 進度文字距載入條
const LOADING_TITLE_SIZE = 42; // 載入標題字級
const LOADING_LABEL_SIZE = 20; // 進度字級
const LOADING_TEXT_COLOR = '#fff5d6'; // 暖米白
const LOADING_FONT = '"PingFang TC", "Microsoft JhengHei", sans-serif'; // 共用中文字型

export class CBootScene extends Phaser.Scene {
  constructor() {
    super({ key: SceneKey.Boot });
  }

  preload(): void {
    // ============ 進度條視覺（圖片未載入前即可呈現）============
    createTableBackground(this);
    const { width, height } = this.scale;
    const barX = (width - LOADING_BAR_WIDTH) / 2;
    const barY = height / 2 + LOADING_BAR_Y_OFFSET;
    this.add
      .rectangle(barX, barY, LOADING_BAR_WIDTH, LOADING_BAR_HEIGHT, TABLE_THEME.gradientBottom)
      .setOrigin(0, 0.5)
      .setStrokeStyle(1, TABLE_THEME.borderColor);
    const fill = this.add
      .rectangle(
        barX + LOADING_BAR_INSET,
        barY,
        0,
        LOADING_BAR_HEIGHT - LOADING_BAR_INSET * 2,
        TABLE_THEME.borderColor
      )
      .setOrigin(0, 0.5);
    this.add
      .text(width / 2, barY - LOADING_TITLE_OFFSET, '至尊麻將王', {
        fontFamily: LOADING_FONT,
        fontSize: `${LOADING_TITLE_SIZE}px`,
        fontStyle: 'bold',
        color: LOADING_TEXT_COLOR,
      })
      .setOrigin(0.5);
    const label = this.add
      .text(width / 2, barY + LOADING_LABEL_OFFSET, '載入中… 0%', {
        fontFamily: LOADING_FONT,
        fontSize: `${LOADING_LABEL_SIZE}px`,
        color: LOADING_TEXT_COLOR,
      })
      .setOrigin(0.5);
    this.load.on('progress', (value: number) => {
      fill.width = (LOADING_BAR_WIDTH - LOADING_BAR_INSET * 2) * value;
      label.setText(`載入中… ${Math.round(value * 100)}%`);
    });

    // ============ UI 素材 ============
    // 金幣圖示與商店商品圖（Codex 產圖，見 docs/Shop-Help-Design.md）
    this.load.image(COIN_ICON_KEY, 'assets/ui/coin.png');
    for (const character of CHARACTERS) this.load.image(avatarKey(character.id), character.avatar);
    this.load.image(SHOP_CARD_KEY, 'assets/ui/shop-card.png');
    this.load.image(SHOP_PACK_S_KEY, 'assets/ui/shop-pack-s.png');
    this.load.image(SHOP_PACK_M_KEY, 'assets/ui/shop-pack-m.png');
    this.load.image(SHOP_PACK_L_KEY, 'assets/ui/shop-pack-l.png');
    this.load.image('home-bg', 'assets/ui/home-bg.png');
    this.load.image('table-a', 'assets/ui/table-a.png');
    this.load.image('btn-normal', 'assets/ui/btn-normal.png');
    this.load.image('btn-press', 'assets/ui/btn-press.png');
    this.load.image(ICON_DEALER_KEY, 'assets/ui/icon-dealer.png');
    this.load.spritesheet(THINKING_ICON_KEY, 'assets/ui/icon-thinking.png', {
      frameWidth: 252,
      frameHeight: 351,
    });
    this.load.image('tenpai-hint-bg', 'assets/ui/tenpai-hint-bg.png');
    this.load.image('role-focus', 'assets/ui/role-focus.png');
    this.load.image('discard-bg', 'assets/ui/discard-bg.png');
    this.load.image(RESULT_WIN_KEY, 'assets/ui/result-win.png');
    this.load.image(RESULT_LOSE_KEY, 'assets/ui/result-lose.png');
    this.load.image(RESULT_DRAW_KEY, 'assets/ui/result-draw.png');
    this.load.image('btn-ai-proxy', 'assets/ui/btn-ai-proxy.png');

    // ---- 牌局結束三畫面（結算流程）----
    this.load.spritesheet(RESULT_AVATAR_KEY, 'assets/ui/result-avatar.png', {
      frameWidth: RESULT_AVATAR_FRAME_SIZE,
      frameHeight: RESULT_AVATAR_FRAME_SIZE,
    });
    this.load.spritesheet(RESULT_SEAT_TEXT_KEY, 'assets/ui/result-seat-text.png', {
      frameWidth: RESULT_SEAT_TEXT_FRAME_WIDTH,
      frameHeight: RESULT_SEAT_TEXT_FRAME_HEIGHT,
    });
    this.load.spritesheet(RESULT_WIND_KEY, 'assets/ui/result-wind.png', {
      frameWidth: RESULT_WIND_FRAME_SIZE,
      frameHeight: RESULT_WIND_FRAME_SIZE,
    });
    this.load.spritesheet(RESULT_TABLE_KEY, 'assets/ui/result-table.png', {
      frameWidth: RESULT_TABLE_FRAME_WIDTH,
      frameHeight: RESULT_TABLE_FRAME_HEIGHT,
    });
    this.load.image(RESULT_TITLE_KEY, 'assets/ui/result-title.png');
    this.load.image(RESULT_NPC_KEY, 'assets/ui/result-npc.png');
    this.load.image(RESULT_NPC_BUBBLE_KEY, 'assets/ui/result-npc-bubble.png');
    this.load.image(RESULT_NPC_TIP_KEY, 'assets/ui/result-npc-tip.png');
    for (const asset of CREAM_ASSETS) this.load.image(asset.key, asset.path);
    this.load.image(RESULT_DIALOG_KEY, 'assets/ui/result-dialog.png');
    this.load.image(RESULT_TAG_SELF_DRAW_KEY, 'assets/ui/result-tag-self-draw.png');
    this.load.image(RESULT_TAG_HU_KEY, 'assets/ui/result-tag-hu.png');
    this.load.image(RESULT_TAG_PAO_KEY, 'assets/ui/result-tag-pao.png');
    this.load.spritesheet(RESULT_NUMBER_SYMBOL_KEY, 'assets/ui/result-number-symbol.png', {
      frameWidth: RESULT_NUMBER_SYMBOL_FRAME_WIDTH,
      frameHeight: RESULT_NUMBER_SYMBOL_FRAME_HEIGHT,
    });
    this.load.image(RESULT_BTN_NORMAL_KEY, 'assets/ui/result-btn-normal.png');
    this.load.image(RESULT_BTN_PRESS_KEY, 'assets/ui/result-btn-press.png');
    this.load.image(RESULT_BTN_CONTINUE_KEY, 'assets/ui/result-btn-continue.png');
    this.load.image(RESULT_BTN_EXIT_KEY, 'assets/ui/result-btn-exit.png');
    this.load.image(RESULT_BTN_HOVER_KEY, 'assets/ui/result-btn-hover.png');

    // ============ 首頁（CHomeScene）素材 ============
    // 來源：asset/reference/main_*.png（至尊麻將王 APP 原始標題畫面拆圖）
    this.load.image(HOME_MAIN_BG_KEY, 'assets/ui/home-main-bg.png'); // 紫色漸層底（橫向鋪滿）
    this.load.image(HOME_CURTAIN_TOP_KEY, 'assets/ui/home-curtain-top.png'); // 上方紅幕簾頭
    this.load.image(HOME_CURTAIN_SIDE_KEY, 'assets/ui/home-curtain-side.png'); // 側邊紅幕（左/右鏡像）
    this.load.image(HOME_TITLE_KEY, 'assets/ui/home-title.png'); // 「至尊麻將王」logo
    this.load.image(HOME_NPC_KEY, 'assets/ui/home-npc.png'); // 兔女郎角色
    // 麻將牌裝飾：684×504 的 4×2 grid → spritesheet（每格 171×252），8 張牌散成扇形排在角色身後
    this.load.spritesheet(HOME_CARDS_KEY, 'assets/ui/home-cards.png', {
      frameWidth: 171,
      frameHeight: 252,
    });
    this.load.image('home-btn-start-text', 'assets/ui/home-btn-start-text.png'); // 「開始遊戲」字
    this.load.image('home-btn-setting', 'assets/ui/home-btn-setting.png'); // 設定齒輪鈕
    this.load.image(HOME_BUTTON_START_FRAME_KEY, 'assets/ui/home-btn-start-frame.png');
    this.load.image(HOME_BUTTON_SMALL_FRAME_KEY, 'assets/ui/home-btn-small-frame.png');
    this.load.image(HOME_BUTTON_GENERIC_FRAME_KEY, 'assets/ui/home-btn-generic-frame.png');
    // 兔耳：315×234 直向 2-frame spritesheet（每格 315×117），首頁取 frame 0
    this.load.spritesheet(HOME_NPC_EARS_KEY, 'assets/ui/home-npc-ears.png', {
      frameWidth: 315,
      frameHeight: 117,
    });
    // 星星：162×216 的 3×4 grid → spritesheet（每格 54×54，共 12 frames），兔女郎頭部往外擴散用
    this.load.spritesheet(HOME_FOCUS_STAR_KEY, 'assets/ui/focus-star.png', {
      frameWidth: 54,
      frameHeight: 54,
    });

    // table-text：4×4 spritesheet（每格 54×54），共 4 列 4 行
    //   row 1 (frames  0–3 )：東 南 西 北（normal，目前風位徽章使用）
    //   row 2 (frames  4–7 )：東 南 西 北（active，預留給「輪到誰」高亮）
    //   row 3 (frames  8–11)：風 局 底 台（預留 — 未來桌面中央顯示「東風一局」之類）
    //   row 4 (frames 12–15)：東 南 西 北（另一種色系，預留）
    this.load.spritesheet(TABLE_TEXT_KEY, 'assets/ui/table-text.png', {
      frameWidth: TABLE_TEXT_FRAME_SIZE,
      frameHeight: TABLE_TEXT_FRAME_SIZE,
    });

    // 計數字體圖：1×11 spritesheet，frame 0–9 數字，frame 10 逗號
    this.load.spritesheet(NUMBER_4_KEY, 'assets/ui/number-4.png', {
      frameWidth: NUMBER_4_FRAME_WIDTH,
      frameHeight: NUMBER_4_FRAME_HEIGHT,
    });
    this.load.image(WORD_X_KEY, 'assets/ui/word-x.png');
    this.load.image(CARD_FRAME_KEY, 'assets/ui/card-frame.png');
    this.load.spritesheet(OPTION_KEY, 'assets/ui/option.png', {
      frameWidth: OPTION_FRAME_WIDTH,
      frameHeight: OPTION_FRAME_HEIGHT,
    });
    this.load.image(TABLE_LOGO_KEY, 'assets/ui/table-logo.png');
    this.load.spritesheet(TABLE_NUMBER_KEY, 'assets/ui/table-number.png', {
      frameWidth: TABLE_NUMBER_FRAME_WIDTH,
      frameHeight: TABLE_NUMBER_FRAME_HEIGHT,
    });
    // 回合指示手勢：2×2 spritesheet（每格 144×126）
    this.load.spritesheet(DIRECTION_HAND_KEY, 'assets/ui/direction-hand.png', {
      frameWidth: DIRECTION_HAND_FRAME_WIDTH,
      frameHeight: DIRECTION_HAND_FRAME_HEIGHT,
    });

    this.load.spritesheet(ACTION_ICON_KEY, 'assets/ui/icons-action.png', {
      frameWidth: ACTION_ICON_FRAME_WIDTH,
      frameHeight: ACTION_ICON_FRAME_HEIGHT,
    });

    // 補花橫幅素材（CFlowerBanner）：徽章 + 兩色梅花 + 漸層花帶底
    this.load.image(FLOWER_EMBLEM_KEY, 'assets/ui/flower-emblem.png');
    this.load.image(FLOWER_PETAL_A_KEY, 'assets/ui/flower-petal-a.png');
    this.load.image(FLOWER_PETAL_B_KEY, 'assets/ui/flower-petal-b.png');
    this.load.image(FLOWER_BAND_KEY, 'assets/ui/flower-band.png');

    // 聽牌模式素材：瞇牌手掌 / 拇指 + 宣告聽橫幅
    this.load.image(PEEK_HAND_KEY, 'assets/ui/peek-hand.png');
    this.load.image(PEEK_THUMB_KEY, 'assets/ui/peek-thumb.png');
    this.load.image(TENPAI_BANNER_KEY, 'assets/ui/tenpai-banner.png');
    this.load.spritesheet(TENPAI_FACE_KEY, 'assets/ui/tenpai-face.png', {
      frameWidth: TENPAI_FACE_FRAME_WIDTH,
      frameHeight: TENPAI_FACE_FRAME_HEIGHT,
    });
    this.load.spritesheet(TENPAI_STAR_KEY, 'assets/ui/tenpai-star.png', {
      frameWidth: TENPAI_STAR_FRAME_WIDTH,
      frameHeight: TENPAI_STAR_FRAME_HEIGHT,
    });

    // ============ TTile sprites ============
    // tiles-source 是 6×7 grid 的 42 張牌面（man → pin → sou → wind → dragon → flower）
    this.load.spritesheet(TILE_SHEET_KEY, 'assets/tiles/tiles-source.png', {
      frameWidth: TILE_FRAME_WIDTH,
      frameHeight: TILE_FRAME_HEIGHT,
    });

    // 牌背 / 側邊（顯示其他三家手牌時用）
    this.load.image('tile-blank-front', 'assets/tiles/tile-blank-front.png');
    this.load.image('tile-back-up', 'assets/tiles/tile-back-up.png');
    this.load.image('tile-back-down', 'assets/tiles/tile-back-down.png');
    this.load.image('tile-side-up', 'assets/tiles/tile-side-up.png');
    this.load.image('tile-side-down', 'assets/tiles/tile-side-down.png');

    // 海底捨牌專用底圖（63×99 portrait，一般捨牌藍邊）
    this.load.image('tile-front-down', 'assets/tiles/tile-front-down.png');
    // 被吃/碰/槓的海底牌覆蓋圖（tile_mask 63×99 牌形深灰遮罩）— 蓋在被 claim 的捨牌上
    this.load.image('discard-claimed', 'assets/ui/discard-claimed.png');

    // ============ 擲骰開局過場素材 ============
    this.load.image(DICE_DISH_KEY, 'assets/dice/dice-dish.png');
    this.load.spritesheet(DICE_FACES_KEY, 'assets/dice/dice-faces.png', {
      frameWidth: DICE_FACE_FRAME_SIZE,
      frameHeight: DICE_FACE_FRAME_SIZE,
    });
    this.load.spritesheet(DICE_ROLL_KEY, 'assets/dice/dice-roll.png', {
      frameWidth: DICE_ROLL_FRAME_SIZE,
      frameHeight: DICE_ROLL_FRAME_SIZE,
    });
    this.load.spritesheet(ROUND_WORDS_KEY, 'assets/dice/round-words.png', {
      frameWidth: ROUND_WORDS_FRAME_SIZE,
      frameHeight: ROUND_WORDS_FRAME_SIZE,
    });
    this.load.audio(SFX_DICE_KEY, 'assets/audio/dice.mp3');
    for (const prevailingWind of Object.values(Wind)) {
      for (const roundWind of Object.values(Wind)) {
        this.load.audio(
          SFX_OPEN_WIND_KEY_BY_ROUND[prevailingWind][roundWind],
          `assets/audio/rounds/round_${prevailingWind}_${roundWind}.mp3`
        );
      }
    }
    this.load.audio(SFX_RESULT_MONEY_KEY, 'assets/audio/result-money.ogg');
    this.load.audio(AudioKey.TileStack, 'assets/audio/tile-stack.mp3');
    this.load.audio(AudioKey.Tenpai, 'assets/audio/tenpai.mp3');
    this.load.audio(AudioKey.UIClick, 'assets/audio/ui-click.mp3');
    this.load.audio(AudioKey.BGMLobby, 'assets/audio/bgm-lobby.mp3');
    this.load.audio(AudioKey.BGMTable, 'assets/audio/bgm-table.mp3');
    this.load.audio(AudioKey.BGMTenpai, 'assets/audio/bgm-tenpai.mp3');
    for (const pack of VOICE_PACKS) {
      for (const cue of VOICE_CUES) {
        this.load.audio(voiceAudioKey(pack, cue), `assets/audio/voices/${pack}/${cue}.mp3`);
      }
    }
    for (const pack of START_VOICE_PACKS) {
      for (const cue of START_VOICE_CUES) {
        this.load.audio(
          startVoiceAudioKey(pack, cue),
          `assets/audio/start-voices/${pack}/${cue}.mp3`
        );
      }
    }

    // ============ 胡牌／自摸儀式素材 ============
    this.load.image(ROUND_SELF_DRAW_KEY, 'assets/ui/round-self-draw.png');
    this.load.image(ROUND_SELF_DRAW_4X_KEY, 'assets/ui/round-self-draw-4x.png');
    this.load.image(ROUND_HU_KEY, 'assets/ui/round-hu.png');
    this.load.image(ROUND_HU_4X_KEY, 'assets/ui/round-hu-4x.png');
    this.load.image(ROUND_CHUCKER_KEY, 'assets/ui/round-chucker.png');
    this.load.image(ROUND_WIN_FAN_KEY, 'assets/ui/round-win-fan.png');
    this.load.image(ROUND_WIN_BAND_KEY, 'assets/ui/round-win-band.png');
    this.load.spritesheet(ROUND_WIN_STARS_KEY, 'assets/ui/round-win-stars.png', {
      frameWidth: ROUND_WIN_STAR_FRAME_SIZE,
      frameHeight: ROUND_WIN_STAR_FRAME_SIZE,
    });
    // ============ 慶祝粒子（胡／自摸／槓） ============
    for (const key of Object.values(FxKey)) this.load.image(key, `assets/fx/${key}.png`);
    // ============ 流局儀式素材 ============
    this.load.image(ROUND_DRAW_BAND_KEY, 'assets/ui/round-draw-band.png');
    this.load.spritesheet(ROUND_DRAW_WAVES_KEY, 'assets/ui/round-draw-waves.png', {
      frameWidth: ROUND_DRAW_WAVE_FRAME_WIDTH,
      frameHeight: ROUND_DRAW_WAVE_FRAME_HEIGHT,
    });
    this.load.image(ROUND_DRAW_MARK_KEY, 'assets/ui/round-draw-mark.png');
  }

  create(): void {
    for (const asset of CREAM_ASSETS) {
      this.textures
        .get(asset.key)
        .add(CREAM_ASSET_FRAME, 0, asset.x, asset.y, asset.width, asset.height);
    }

    // 全域主音量：SoundManager 為 game 層級單例，設一次即套用到後續所有場景
    this.sound.setVolume(DEFAULT_MASTER_VOLUME);

    // 開發 / UAT 用：讀 URL 參數略過 Home 直接進指定 Scene
    // - ?scene=Game                  → 進 GameScene
    // - ?scene=GameSetup             → 底台選擇預覽（不寫入正式 localStorage）
    // - ?scene=Dice                  → 進 CDiceScene 預覽（擲骰動畫，不自動進牌局；R 重播）
    // - ?scene=ResultTile            → 牌局結束畫面 1：各家狀態（無 payload → 注入 mock，可往下串接）
    // - ?scene=ResultPoint           → 牌局結束畫面 2：算台數
    // - ?scene=ResultMoney           → 牌局結束畫面 3：籌碼結算
    // - ?scene=SessionSummary        → 跨局牌局組總結算預覽
    // - ?scene=Shop                  → 商店
    // - ?preview=1（不帶 scene）     → 視為 ?scene=Game&preview=1（preview 旗標由 GameScene 自行讀）
    // 合法 scene 值：Home / GameSetup / Dice / Game / ResultTile / ResultPoint / ResultMoney / SessionSummary / Shop
    const params = new URLSearchParams(window.location.search);
    const requested = params.get('scene');
    const isPreview = params.get('preview') === '1';
    const target =
      requested === SceneKey.Game ||
      requested === SceneKey.TableSelect ||
      requested === SceneKey.GameSetup ||
      requested === SceneKey.Dice ||
      requested === SceneKey.ResultTile ||
      requested === SceneKey.ResultPoint ||
      requested === SceneKey.ResultMoney ||
      requested === SceneKey.SessionSummary ||
      requested === SceneKey.Shop
        ? requested
        : isPreview
          ? SceneKey.Game
          : SceneKey.Home;
    this.scene.start(target);
  }
}
